import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAllUsers, createUserManagement, updateUserManagement, deleteUserManagement } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Users, Loader2, Trash2, Edit, Shield, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const GestionUsuarios = () => {
  const { isDirection, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'recepcion', password: '' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await getAllUsers();
      setUsers(res.data);
    } catch (e) { console.error('Error cargando usuarios:', e.message); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        const update = { name: form.name, email: form.email, role: form.role };
        if (form.password) update.password = form.password;
        await updateUserManagement(editingUser.id, update);
        toast.success('Usuario actualizado');
      } else {
        if (!form.password) { toast.error('La contraseña es requerida'); setSaving(false); return; }
        await createUserManagement(form);
        toast.success('Usuario creado');
      }
      setShowDialog(false);
      setEditingUser(null);
      setForm({ name: '', email: '', role: 'recepcion', password: '' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este usuario?')) return;
    try {
      await deleteUserManagement(id);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({ name: user.name, email: user.email, role: user.role, password: '' });
    setShowDialog(true);
  };

  if (!isDirection()) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Users className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
          <h2 className="text-xl font-semibold text-[#1A2835]">Acceso Restringido</h2>
          <p className="text-[#5C7083]">Solo Dirección puede gestionar usuarios</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gestion-usuarios-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Gestión de Usuarios</h1>
          <p className="page-subtitle">Administrar cuentas y permisos del equipo</p>
        </div>
        <Button className="btn-primary gap-2" onClick={() => { setEditingUser(null); setForm({ name: '', email: '', role: 'recepcion', password: '' }); setShowDialog(true); }} data-testid="btn-new-user">
          <Plus size={18} /> Nuevo Usuario
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#F5F0E8] rounded-lg"><Users className="text-[#2B4257]" size={24} /></div>
          <div>
            <p className="text-2xl font-semibold">{users.length}</p>
            <p className="text-sm text-[#5C7083]">Total Usuarios</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#EDF5F0] rounded-lg"><ShieldCheck className="text-[#4A7C59]" size={24} /></div>
          <div>
            <p className="text-2xl font-semibold">{users.filter(u => u.role === 'direccion').length}</p>
            <p className="text-sm text-[#5C7083]">Dirección</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#FAF0EC] rounded-lg"><Shield className="text-[#D98A6C]" size={24} /></div>
          <div>
            <p className="text-2xl font-semibold">{users.filter(u => u.role === 'recepcion').length}</p>
            <p className="text-sm text-[#5C7083]">Recepción</p>
          </div>
        </div>
      </div>

      <div className="card p-0 overflow-hidden" data-testid="users-table">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium text-[#1A2835]">{u.name}</td>
                  <td>{u.email}</td>
                  <td>
                    <span className={`status-badge ${u.role === 'direccion' ? 'status-activo' : 'status-pendiente'}`}>
                      {u.role === 'direccion' ? 'Dirección' : 'Recepción'}
                    </span>
                  </td>
                  <td className="text-sm text-[#5C7083]">{u.created_at?.slice(0, 10)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="btn-ghost p-2" onClick={() => handleEdit(u)} data-testid={`btn-edit-user-${u.id}`}>
                        <Edit size={16} />
                      </Button>
                      {u.id !== currentUser?.id && (
                        <Button variant="ghost" size="sm" className="btn-ghost p-2 text-[#C25953]" onClick={() => handleDelete(u.id)} data-testid={`btn-delete-user-${u.id}`}>
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4" data-testid="user-form">
            <div>
              <Label className="form-label">Nombre *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="form-input" required data-testid="input-user-name" />
            </div>
            <div>
              <Label className="form-label">Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="form-input" required data-testid="input-user-email" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Rol</Label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="form-input" data-testid="select-user-role">
                  <option value="recepcion">Recepción</option>
                  <option value="direccion">Dirección</option>
                </select>
              </div>
              <div>
                <Label className="form-label">{editingUser ? 'Nueva Contraseña' : 'Contraseña *'}</Label>
                <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="form-input"
                  required={!editingUser} placeholder={editingUser ? 'Dejar vacío para no cambiar' : ''} data-testid="input-user-password" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving || !form.name || !form.email} data-testid="btn-save-user">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingUser ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionUsuarios;
